import { Card, CardContent } from "@material-ui/core";
import classnames from "classnames";
import { parseISO } from "date-fns";
import { useMemo } from "react";
import { useCallback } from "react";
import { useNotify, useTranslate } from "react-admin";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import {
  DriverLogDetailedView,
  EventApiFactory,
} from "../../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../../common/hooks/useAxiosInstance";
import { CompactFormToolbar } from "../../../../common/form/CompactFormToolbar";
import { CompactSaveButton } from "../../../../common/form/CompactSaveButton";
import { HookFormDriverPicker } from "../../../../common/form/hook-form/inputs/HookFormDriverPicker";
import { HookFormEventPicker } from "../../../../common/form/hook-form/inputs/HookFormEventPicker";
import { handleAxiosError } from "../../../../../common/data/axiosErrorHandler";

interface Props {
  record: DriverLogDetailedView;
  onSuccess: () => void;
  onFailed?: () => void;
}

type FormValues = {
  startEvent: number;
  endEvent: number;
  driverId: number;
};

const DefaultCompactFormToolbar = () => {
  const {
    formState: { isSubmitting, isValid },
  } = useFormContext();
  return (
    <CompactFormToolbar>
      <CompactSaveButton
        saving={isSubmitting}
        disabled={isSubmitting || !isValid}
      />
    </CompactFormToolbar>
  );
};

export const ReassignEventsForm = ({
  record,
  onSuccess,
  onFailed,
  ...rest
}: Props) => {
  const translate = useTranslate();
  const notify = useNotify();
  const axios = useAxiosInstance();

  const onSubmit = useCallback(
    (data: FormValues) => {
      if (!axios) {
        return;
      }

      return EventApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppEventReassigneventsPost(data)
        .then(() => {
          onSuccess();
        })
        .catch((e) => {
          if (onFailed) {
            onFailed();
          }

          console.error(e);
          notify(handleAxiosError(e), { type: "error" });
        });
    },
    [axios, notify, onSuccess, onFailed]
  );

  const formContext = useForm<FormValues>({
    mode: "onChange",
  });

  const { handleSubmit, control } = formContext;

  const handleSubmitInternal: SubmitHandler<FormValues> = (data) => {
    return Promise.resolve(onSubmit(data)).catch((e: Error) => {
      notify(e.message ?? "An unexpected error has occurred", "error");
    });
  };

  const logDate = useMemo(
    () => (record.date ? parseISO(record.date) : undefined),
    [record.date]
  );

  return (
    <CardContent>
      <FormProvider {...formContext}>
        <form
          className={classnames("simple-form", "form-create-edit-drawer")}
          {...sanitizeRestProps(rest)}
          onSubmit={handleSubmit(handleSubmitInternal, (err) =>
            console.error(err)
          )}>
          <Card>
            <CardContent>
              <div className="ra-input">
                <HookFormEventPicker<FormValues>
                  control={control}
                  name="startEvent"
                  label="resources.hos-driver-log-view.labels.startEvent"
                  rules={{ required: translate("ra.validation.required") }}
                  variant="outlined"
                  defaultValue=""
                  timeZone={record.homeTerminalTimeZone!}
                  driverId={record.driverId!}
                  initialDate={logDate}
                />
              </div>
              <div className="ra-input">
                <HookFormEventPicker<FormValues>
                  control={control}
                  name="endEvent"
                  label="resources.hos-driver-log-view.labels.endEvent"
                  rules={{ required: translate("ra.validation.required") }}
                  variant="outlined"
                  defaultValue=""
                  timeZone={record.homeTerminalTimeZone!}
                  driverId={record.driverId!}
                  initialDate={logDate}
                />
              </div>
              <div className="ra-input">
                <HookFormDriverPicker<FormValues>
                  control={control}
                  name="driverId"
                  label="resources.hos-driver-log-view.labels.driver"
                  rules={{ required: translate("ra.validation.required") }}
                  variant="outlined"
                  defaultValue={null}
                />
              </div>
            </CardContent>
          </Card>
          <DefaultCompactFormToolbar />
        </form>
      </FormProvider>
    </CardContent>
  );
};

const sanitizeRestProps = ({
  active,
  dirty,
  dirtyFields,
  dirtyFieldsSinceLastSubmit,
  dirtySinceLastSubmit,
  error,
  errors,
  form,
  hasSubmitErrors,
  hasValidationErrors,
  initialValues,
  modified = null,
  modifiedSinceLastSubmit,
  save = null,
  submitError,
  submitErrors,
  submitFailed,
  submitSucceeded,
  submitting,
  touched = null,
  valid,
  values,
  visited = null,
  __versions = null,
  ...props
}: any) => props;
